import { ACTION_STATUSES, ERROR_PATH } from 'shared/consts';
import {
  AppThemeProvider,
  DefaultThemeWithFonts,
  defaultTheme,
} from '@hqo/react-components-library/dist/molecules/theme';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AppContentWrapperStyles } from './app-content-wrapper.styles';
import { Container } from 'hocs/shared-styles';
import { CustomFonts } from 'components/custom-fonts';
import { Spinner } from '@hqo/react-components-library/dist/atoms/spinner';
import { authTokenSelector } from 'store/config/selectors';
import { selectBuildingStatus } from 'store/building/selectors';
import { showGenericErrorNotification } from 'store/errors/actions';
import { useBuilding } from 'hooks/use-building.hook';
import { useBuildingTheme } from 'hooks/use-building-theme.hook';
import { useLocation } from 'react-router-dom';
import { useViewParams } from 'hooks/use-view-params.hook';

const INITIALIZATION_TIMEOUT_MS = 20 * 1000; // 20 seconds

interface AppContentWrapperProps {
  children: React.ReactNode;
}

export const AppContentWrapper = ({ children }: AppContentWrapperProps): JSX.Element => {
  const dispatch = useDispatch();
  const location = useLocation();
  const buildingStatus = useSelector(selectBuildingStatus);
  const authToken = useSelector(authTokenSelector);
  const params = useViewParams();
  const theme = useBuildingTheme();
  const building = useBuilding();
  const [hasInitTimedOut, setHasInitTimedOut] = useState(false);

  const buildingStatusFinished =
    buildingStatus === ACTION_STATUSES.FULFILLED || buildingStatus === ACTION_STATUSES.REJECTED;

  const hasBuilding = !!building || !!params.buildingUuid;
  const hasMissingAuth = !authToken && !building && buildingStatus !== ACTION_STATUSES.PENDING;
  const isErrorPage = location.pathname === ERROR_PATH;
  const isInitialized = theme && (buildingStatusFinished || !hasBuilding);

  // Initialization timeout - show error if app is stuck loading too long
  useEffect(() => {
    if (isInitialized || isErrorPage) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setHasInitTimedOut(true);
    }, INITIALIZATION_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isInitialized, isErrorPage]);

  // Show error if initialization timed out
  useEffect(() => {
    if (hasInitTimedOut && !isInitialized && !isErrorPage) {
      dispatch(
        showGenericErrorNotification({
          action: 'INITIALIZATION_TIMEOUT',
          error: { name: 'TimeoutError', message: 'App initialization timed out' },
        }),
      );
    }
  }, [dispatch, hasInitTimedOut, isInitialized, isErrorPage]);

  // Show error if no auth token and no building data (initialization failed)
  useEffect(() => {
    if (hasMissingAuth) {
      dispatch(
        showGenericErrorNotification({
          action: 'MISSING_AUTH_TOKEN',
          error: { name: 'AuthError', message: 'No authentication token available' },
        }),
      );
    }
  }, [dispatch, hasMissingAuth]);

  // Always render error page with default theme (don't block with spinner)
  if (isErrorPage) {
    return <AppThemeProvider theme={(theme || defaultTheme) as DefaultThemeWithFonts}>{children}</AppThemeProvider>;
  }

  if (isInitialized) {
    return (
      <>
        <AppContentWrapperStyles customThemeFonts={theme.fonts} />
        <CustomFonts />
        <AppThemeProvider theme={theme as DefaultThemeWithFonts}>{children}</AppThemeProvider>
      </>
    );
  }

  return (
    <Container data-testid="loading-container" isLoading>
      <Spinner size="10em" color={defaultTheme.colors.$greyLight} />
    </Container>
  );
};
